import { useMemo, useContext, useCallback, createContext } from 'react';

import {
  CourseStep,
  CourseWithSteps,
  CourseStepStatus,
  CourseStepSubmission,
  CourseSubmissionStatus,
} from 'types';

import { useTranslationContext } from '../translation';

import { CourseContextProps, CourseContextProviderProps } from './types';

const CourseContext = createContext<CourseContextProps>({
  language: 'en',
  isLoaded: false,
  isLoading: false,
  hasCourse: false,
  isFinished: false,
  useLargeCells: false,
  getStep: () => undefined,
  getStepStatus: () => CourseStepStatus.Locked,
});

export const CourseContextProvider = ({
  steps,
  course,
  children,
  isLoaded,
  isLoading,
  submission,
  submissions,
}: CourseContextProviderProps) => {
  const { language } = useTranslationContext();
  const stepsMap = useMemo(() => {
    const map = new Map<string, CourseStep>();

    steps?.forEach((step) => {
      map.set(step.id, step);
    });

    return map;
  }, [steps]);

  const isFinished = submission?.status === CourseSubmissionStatus.COMPLETED;

  const getStep = useCallback(
    (stepId: string) => stepsMap.get(stepId),
    [stepsMap]
  );

  const { hasCourse, courseWithSteps } = useMemo(() => {
    const hasCourse = !!course && !!steps;

    const courseWithSteps: CourseWithSteps | undefined = hasCourse
      ? {
          ...course,
          steps: course.stepIds
            .map((id) => {
              return steps.find((step) => step.id === id);
            })
            .filter(Boolean) as CourseStep[],
        }
      : undefined;

    if (
      courseWithSteps &&
      courseWithSteps.steps.length !== course?.stepIds.length
    ) {
      return {
        hasCourse: false,
        courseWithSteps: undefined,
      };
    }

    return { hasCourse, courseWithSteps };
  }, [course, steps]);

  const useLargeCells = !!courseWithSteps && courseWithSteps.steps.length <= 4;

  const statuses = useMemo(() => {
    const map = new Map<string, CourseStepStatus>();
    if (!courseWithSteps || !submissions) {
      return map;
    }

    const submissionsMap = new Map<string, CourseStepSubmission>();
    submissions?.forEach((submission) => {
      submissionsMap.set(submission.courseStepId, submission);
    });

    let foundFirstActive = false;

    courseWithSteps.steps.forEach((step) => {
      const submission = submissionsMap.get(step.id);

      if (submission?.isSubmitted) {
        map.set(step.id, CourseStepStatus.Completed);
      } else if (!foundFirstActive) {
        map.set(step.id, CourseStepStatus.Active);
        foundFirstActive = true;
      } else {
        map.set(step.id, CourseStepStatus.Locked);
      }
    });
    return map;
  }, [courseWithSteps, submissions]);

  const getStepStatus = useCallback(
    (stepId: string) => {
      return statuses.get(stepId) || CourseStepStatus.Locked;
    },
    [statuses]
  );

  return (
    <CourseContext.Provider
      value={{
        getStep,
        isLoaded,
        language,
        hasCourse,
        isLoading,
        isFinished,
        submission,
        useLargeCells,
        getStepStatus,
        rawCourse: course,
        course: courseWithSteps,
        submissionId: submission?.id,
        steps: courseWithSteps?.steps,
      }}
    >
      {children}
    </CourseContext.Provider>
  );
};

export const useCourseContext = () => {
  return useContext(CourseContext);
};
